import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { getShippingZipPrefill } from 'vivino-js/helpers/shipToHelper';
import t from 'vivino-js/translationString';
import RotatingChevron from 'vivino-ui/atoms/RotatingChevron';

import ZipCodeInput from 'vivino-ui/components/ZipCodeInput';

import styles from './zipCodeDrawer.scss';

const ZIP_CODE_ANIMATION_DURATION = 300;

const TRANSLATIONS_PATH = 'common';
const TRANSLATIONS = {
  byProvidingYourZipCode: `${TRANSLATIONS_PATH}.by_providing_your_zip_code`,
  enterYourZipCode: `${TRANSLATIONS_PATH}.enter_your_zip_code`,
  shippingToX: `${TRANSLATIONS_PATH}.shipping_to_x`,
  shippingAndDeliveryEstimate: `${TRANSLATIONS_PATH}.shipping_and_delivery_estimate`,
};

const ZipCodeDrawer = ({
  countryCode,
  shippingZipCode = '',
  requireShippingZipText = '',
  onZipValidated,
  onZipInvalidated,
  isOpen = false,
}) => {
  const [isDrawn, setIsDrawn] = useState(isOpen);
  const [headerText, setHeaderText] = useState('');
  const [shippingLocation, setShippingLocation] = useState(getShippingZipPrefill() || {});

  useEffect(() => {
    const city = shippingLocation?.city;
    const state = shippingLocation?.state;
    setHeaderText(getPrefilledShippingText({ city, state }));
  }, [shippingLocation]);

  const handleClick = () => {
    setIsDrawn((prevValue) => !prevValue);
  };

  const handleZipValidated = (data) => {
    setIsDrawn(false);

    const shipToData = data?.ship_to;

    if (shipToData) {
      setShippingLocation(shipToData.zip_prefill);

      onZipValidated?.(shipToData.zip_code);
    }
  };

  const getPrefilledShippingText = ({ city = '', state = '' }) => {
    if (city) {
      return t(TRANSLATIONS.shippingToX, {
        shipping_locality: `${city}, ${String(state).toUpperCase()}`,
      });
    }

    return t(TRANSLATIONS.shippingAndDeliveryEstimate);
  };

  return (
    <>
      <div
        className={cx(styles.zipCodeDrawer, styles.clickable)}
        onClick={handleClick}
        data-testid="zipCodeDrawer"
      >
        <span className={styles.shippingPolicyText}>{headerText}</span>

        <RotatingChevron isOpen={isDrawn} className={styles.icon} />
      </div>

      <AnimateHeight duration={ZIP_CODE_ANIMATION_DURATION} height={isDrawn ? 'auto' : 0}>
        <div className={styles.zipCodeDrawer}>
          <span className={styles.zipReasonText}>{t(TRANSLATIONS.byProvidingYourZipCode)}</span>
        </div>

        <ZipCodeInput
          shippingZipCode={shippingZipCode}
          requireShippingZipText={requireShippingZipText}
          placeholder={t(TRANSLATIONS.enterYourZipCode)}
          className={styles.zipFormContainer}
          countryCode={countryCode}
          onZipValidated={handleZipValidated}
          onZipInvalidated={onZipInvalidated}
          useMask
          showCountryFlag
        />
      </AnimateHeight>
    </>
  );
};

ZipCodeDrawer.propTypes = {
  countryCode: PropTypes.string,
  shippingZipCode: PropTypes.string,
  requireShippingZipText: PropTypes.string,
  onZipValidated: PropTypes.func,
  onZipInvalidated: PropTypes.func,
  isOpen: PropTypes.bool,
};

export default ZipCodeDrawer;
